import { API_BASE_URL } from '../config';
import { GameSymbol, ComboType, PlayerStatus, RoomStatus } from '@shared/enums';

export { GameSymbol };

export type PickitupTheme = 'classic' | 'royal' | 'neon' | 'forest' | 'midnight' | 'candy';

export interface WeeklyChallengeView {
  id: string;
  icon: string;
  title: string;
  description: string;
  progress: number;
  target: number;
  reward: number;
  completed: boolean;
}

export interface TournamentEntry {
  id: string;
  name: string;
  score: number;
  date: string;
}

export interface ShopItemView {
  id: string;
  title: string;
  description: string;
  price: number;
  owned: boolean;
  equipped: boolean;
  preview: string;
  imageUrl?: string;
}

export interface IconConcept {
  id: string;
  title: string;
  prompt: string;
}

export interface FeatureStateView {
  weekKey: string;
  weekly: WeeklyChallengeView[];
  weeklyCompleted: number;
  coinBalance: number;
  selectedTheme: PickitupTheme;
  ownedIcons: string[];
  iconSlots: Record<GameSymbol, string | null>;
  shopItems: ShopItemView[];
  iconConcepts: IconConcept[];
}

export interface MatchResultPayload {
  name: string;
  score: number;
  rank: number;
  isWinner: boolean;
  isOffline: boolean;
}

const SYMBOL_LIST: GameSymbol[] = [
  GameSymbol.STAR,
  GameSymbol.MOON,
  GameSymbol.HORSESHOE,
  GameSymbol.HEART,
  GameSymbol.CROWN,
  GameSymbol.CLOVER,
];

const DEFAULT_EMOJI: Record<GameSymbol, string> = {
  [GameSymbol.STAR]: '★',
  [GameSymbol.MOON]: '☾',
  [GameSymbol.HORSESHOE]: '∪',
  [GameSymbol.HEART]: '♥',
  [GameSymbol.CROWN]: '♛',
  [GameSymbol.CLOVER]: '♣',
};

const WEEKLY_STORAGE_KEY = 'pickitup_weekly_challenges_v1';
const INVENTORY_STORAGE_KEY = 'pickitup_inventory_v1';
const TOURNAMENT_STORAGE_KEY = 'pickitup_feature_tournament_v1';
const THEME_STORAGE_KEY = 'pickitup_theme_v1';

const SHOP_ICONS: Array<{ id: string; title: string; description: string; preview: string; price: number; imageUrl?: string }> = [
  { id: 'png-pizza-slice', title: 'Pizza Slice', description: 'Ersetzt Stern', price: 350, preview: '/pickitup/shop-icons/pizza-slice.png', imageUrl: '/pickitup/shop-icons/pizza-slice.png' },
  { id: 'png-taco-king', title: 'Taco King', description: 'Ersetzt Mond', price: 350, preview: '/pickitup/shop-icons/taco-king.png', imageUrl: '/pickitup/shop-icons/taco-king.png' },
  { id: 'png-burger-stack', title: 'Burger Stack', description: 'Ersetzt Hufeisen', price: 350, preview: '/pickitup/shop-icons/burger-stack.png', imageUrl: '/pickitup/shop-icons/burger-stack.png' },
  { id: 'png-sushi-roll', title: 'Sushi Roll', description: 'Ersetzt Herz', price: 350, preview: '/pickitup/shop-icons/sushi-roll.png', imageUrl: '/pickitup/shop-icons/sushi-roll.png' },
  { id: 'png-diamond-gem', title: 'Diamond Gem', description: 'Ersetzt Krone', price: 450, preview: '/pickitup/shop-icons/diamond-gem.png', imageUrl: '/pickitup/shop-icons/diamond-gem.png' },
  { id: 'png-rocket-moon', title: 'Rocket Moon', description: 'Ersetzt Mond', price: 380, preview: '/pickitup/shop-icons/rocket-moon.png', imageUrl: '/pickitup/shop-icons/rocket-moon.png' },
  { id: 'png-frog-lucky', title: 'Lucky Frog', description: 'Ersetzt Hufeisen', price: 380, preview: '/pickitup/shop-icons/frog-lucky.png', imageUrl: '/pickitup/shop-icons/frog-lucky.png' },
  { id: 'png-robot-face', title: 'Robot Face', description: 'Ersetzt Stern', price: 380, preview: '/pickitup/shop-icons/robot-face.png', imageUrl: '/pickitup/shop-icons/robot-face.png' },
  { id: 'png-ghost-spooky', title: 'Spooky Ghost', description: 'Ersetzt Kleeblatt', price: 380, preview: '/pickitup/shop-icons/ghost-spooky.png', imageUrl: '/pickitup/shop-icons/ghost-spooky.png' },
  { id: 'png-bitcoin-coin', title: 'Bitcoin Coin', description: 'Ersetzt Krone', price: 450, preview: '/pickitup/shop-icons/bitcoin-coin.png', imageUrl: '/pickitup/shop-icons/bitcoin-coin.png' },
];

const ICON_CONCEPTS_PIPELINE: IconConcept[] = [
  { id: 'pizza-slice', title: 'Pizza Slice', prompt: 'PickItUp slot icon: slice of pepperoni pizza with cheese, bright colors, transparent PNG game asset' },
  { id: 'taco-king', title: 'Taco King', prompt: 'PickItUp slot icon: taco with toppings, bright colors, transparent PNG game asset' },
  { id: 'burger-stack', title: 'Burger Stack', prompt: 'PickItUp slot icon: juicy burger with lettuce and bun, bright colors, transparent PNG game asset' },
  { id: 'sushi-roll', title: 'Sushi Roll', prompt: 'PickItUp slot icon: sushi roll with salmon, bright colors, transparent PNG game asset' },
  { id: 'diamond-ring', title: 'Diamond Ring', prompt: 'PickItUp slot icon: diamond ring sparkling, bright colors, transparent PNG game asset' },
  { id: 'clover-leaf', title: 'Clover Leaf', prompt: 'PickItUp slot icon: four-leaf clover, bright green, transparent PNG game asset' },
  { id: 'moyai-chad', title: 'Moyai Chad', prompt: 'PickItUp slot icon: moyai statue with sunglasses, bright colors, transparent PNG game asset' },
  { id: 'rocket-moon', title: 'Rocket Moon', prompt: 'PickItUp slot icon: rocket flying to moon, bright colors, transparent PNG game asset' },
  { id: 'frog-lucky', title: 'Lucky Frog', prompt: 'PickItUp slot icon: cute green frog, bright colors, transparent PNG game asset' },
  { id: 'skull-heart', title: 'Skull Heart', prompt: 'PickItUp slot icon: skull with heart, bright colors, transparent PNG game asset' },
  { id: 'bitcoin-coin', title: 'Bitcoin', prompt: 'PickItUp slot icon: Bitcoin coin with B symbol, gold, transparent PNG game asset' },
];

const THEMES: Array<{ id: string; theme: PickitupTheme; title: string; description: string; price: number; preview: string }> = [
  { id: 'theme-classic', theme: 'classic', title: 'Classic Neon', description: 'Standard-Look.', price: 0, preview: '🌌' },
  { id: 'theme-royal', theme: 'royal', title: 'Royal Gold', description: 'Gold, Premium, Sieger-Vibe.', price: 900, preview: '👑' },
  { id: 'theme-neon', theme: 'neon', title: 'Cyber Neon', description: 'Cyan, Pink, Arcade-Glow.', price: 1100, preview: '💠' },
  { id: 'theme-forest', theme: 'forest', title: 'Lucky Forest', description: 'Grün, ruhig, Glücksbringer.', price: 950, preview: '🍀' },
  { id: 'theme-midnight', theme: 'midnight', title: 'Midnight', description: 'Dunkel, clean, high contrast.', price: 1250, preview: '🌙' },
  { id: 'theme-candy', theme: 'candy', title: 'Candy Pop', description: 'Bunt, verspielt, hell.', price: 1250, preview: '🍭' },
];

function currentWeekKey(): string {
  const now = new Date();
  const date = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  const day = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  const week = Math.ceil((((date.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  return `${date.getUTCFullYear()}-W${String(week).padStart(2, '0')}`;
}

function todayKey(): string { return new Date().toISOString().slice(0, 10); }

function readJson<T>(key: string, fallback: T): T {
  try { const raw = localStorage.getItem(key); return raw ? JSON.parse(raw) : fallback; } catch { return fallback; }
}

function writeJson(key: string, value: unknown): void {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* noop */ }
}

interface InventoryState {
  coins: number;
  ownedThemes: PickitupTheme[];
  ownedIcons: string[];
  iconSlots: Record<GameSymbol, string | null>;
  selectedTheme: PickitupTheme;
}

function defaultInventory(): InventoryState {
  const emptySlots: Record<GameSymbol, string | null> = { [GameSymbol.STAR]: null, [GameSymbol.MOON]: null, [GameSymbol.HORSESHOE]: null, [GameSymbol.HEART]: null, [GameSymbol.CROWN]: null, [GameSymbol.CLOVER]: null };
  return { coins: 0, ownedThemes: ['classic'], ownedIcons: [], iconSlots: emptySlots, selectedTheme: 'classic' };
}

function readInventory(): InventoryState {
  const inv = readJson<InventoryState>(INVENTORY_STORAGE_KEY, defaultInventory());
  return {
    coins: Math.max(0, Math.round(Number(inv.coins) || 0)),
    ownedThemes: Array.from(new Set(['classic', ...(inv.ownedThemes || [])])),
    ownedIcons: Array.from(new Set(inv.ownedIcons || [])),
    iconSlots: { ...defaultInventory().iconSlots, ...(inv.iconSlots || {}) },
    selectedTheme: inv.selectedTheme || localStorage.getItem(THEME_STORAGE_KEY) || 'classic',
  };
}

function writeInventory(inv: InventoryState): void {
  writeJson(INVENTORY_STORAGE_KEY, inv);
  localStorage.setItem(THEME_STORAGE_KEY, inv.selectedTheme);
}

function authHeaders(includeJson = false): HeadersInit {
  const headers: Record<string, string> = includeJson ? { 'Content-Type': 'application/json' } : {};
  try {
    const token = localStorage.getItem('pickitup_auth_token') || '';
    if (token) headers.Authorization = `Bearer ${token}`;
  } catch {
    // ignore
  }
  return headers;
}

export function setCoinBalance(coins: number): void {
  const inv = readInventory();
  inv.coins = Math.max(0, Math.round(Number(coins) || 0));
  writeInventory(inv);
}

/**
 * Synchronisiere Wallet, Besitz und Slots vom Server ( /api/shop/wallet ).
 */
export async function syncWalletFromServer(): Promise<{ ok: boolean; message?: string }> {
  try {
    const resp = await fetch(`${API_BASE_URL}/shop/wallet`, { headers: authHeaders() });
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}));
      return { ok: false, message: err.error || 'Wallet sync fehlgeschlagen' };
    }
    const data = await resp.json();
    if (data.success) {
      setServerInventory(data);
      applySelectedTheme();
      return { ok: true, message: 'Wallet synchronisiert' };
    }
    return { ok: false, message: data.error || 'Wallet leer' };
  } catch (err) {
    console.error('[FeatureState] syncWalletFromServer error:', err);
    return { ok: false, message: 'Netzwerkfehler' };
  }
}

/**
 * Kaufe ein Item direkt auf dem Server ( /api/shop/purchase ).
 */
export async function purchaseItemOnServer(itemId: string): Promise<{ success: boolean; message: string; coins?: number }> {
  try {
    const resp = await fetch(`${API_BASE_URL}/shop/purchase`, {
      method: 'POST',
      headers: authHeaders(true),
      body: JSON.stringify({ itemId }),
    });
    const data = await resp.json();
    if (data.success) {
      // Aktualisiere lokalen Coin-Balance wenn zurückgegeben
      if (typeof data.coins === 'number') setCoinBalance(data.coins);
      else await syncWalletFromServer();
      return { success: true, message: data.message || 'Gekauft', coins: data.coins ?? readInventory().coins };
    }
    return { success: false, message: data.error || 'Kauf fehlgeschlagen', coins: readInventory().coins };
  } catch (err) {
    console.error('[FeatureState] purchaseItemOnServer error:', err);
    return { success: false, message: 'Netzwerkfehler', coins: readInventory().coins };
  }
}

/**
 * Equipe ein Icon in einem Symbol-Slot ( /api/shop/equip-icon ).
 */
export async function equipIconOnServer(symbol: string, iconId: string | null): Promise<{ success: boolean; message?: string }> {
  try {
    const body: { symbol: string; iconId?: string } = { symbol };
    if (iconId !== null) body.iconId = iconId;
    const resp = await fetch(`${API_BASE_URL}/shop/equip-icon`, {
      method: 'POST',
      headers: authHeaders(true),
      body: JSON.stringify(body),
    });
    const data = await resp.json();
    if (data.success) {
      // Lokal anpassen: sofort optimistisch
      equipIconForSymbol(symbol as GameSymbol, iconId);
      return { success: true };
    }
    return { success: false, message: data.error || 'Equip fehlgeschlagen' };
  } catch (err) {
    console.error('[FeatureState] equipIconOnServer error:', err);
    return { success: false, message: 'Netzwerkfehler' };
  }
}

function setServerInventory(data: {
  coins: number;
  ownedThemes?: string[];
  ownedIcons?: string[];
  iconSlots?: Record<string, string | null>;
}): void {
  const inv = readInventory();
  inv.coins = Math.max(0, Math.round(Number(data.coins) || 0));
  if (data.ownedThemes) {
    const themes = data.ownedThemes.map(id => (id.startsWith('theme-') ? id.slice(6) : id) as PickitupTheme);
    inv.ownedThemes = Array.from(new Set(['classic', ...themes]));
  }
  if (data.ownedIcons) {
    inv.ownedIcons = [...data.ownedIcons];
  }
  if (data.iconSlots) {
    for (const [sym, iconId] of Object.entries(data.iconSlots)) {
      if (sym in inv.iconSlots) {
        (inv.iconSlots as Record<string, string | null>)[sym] = iconId;
      }
    }
  }
  writeInventory(inv);
}

function readWeeklyProgress(): { weekKey: string; onlineMatches: number; onlineWins: number; onlineScore: number; coinsEarnedOnline: number } {
  const weekKey = currentWeekKey();
  const progress = readJson<{ weekKey: string; onlineMatches: number; onlineWins: number; onlineScore: number; coinsEarnedOnline: number }>(WEEKLY_STORAGE_KEY, { weekKey, onlineMatches: 0, onlineWins: 0, onlineScore: 0, coinsEarnedOnline: 0 });
  if (progress.weekKey !== weekKey) return { weekKey, onlineMatches: 0, onlineWins: 0, onlineScore: 0, coinsEarnedOnline: 0 };
  return { weekKey, onlineMatches: Math.max(0, Math.round(progress.onlineMatches || 0)), onlineWins: Math.max(0, Math.round(progress.onlineWins || 0)), onlineScore: Math.max(0, Math.round(progress.onlineScore || 0)), coinsEarnedOnline: Math.max(0, Math.round(progress.coinsEarnedOnline || 0)) };
}

function writeWeeklyProgress(progress: { weekKey: string; onlineMatches: number; onlineWins: number; onlineScore: number; coinsEarnedOnline: number }): void {
  writeJson(WEEKLY_STORAGE_KEY, progress);
}

function readTournamentEntries(): TournamentEntry[] {
  const dateKey = todayKey();
  return readJson<TournamentEntry[]>(TOURNAMENT_STORAGE_KEY, [])
    .filter(entry => String(entry.date || '').startsWith(dateKey))
    .map(entry => ({ id: String(entry.id || `${entry.name}-${entry.score}-${entry.date}`), name: String(entry.name || 'Spieler').slice(0, 20), score: Math.max(0, Math.round(Number(entry.score) || 0)), date: String(entry.date || new Date().toISOString()) }))
    .sort((a, b) => b.score - a.score)
    .slice(0, 20);
}

function writeTournamentEntries(entries: TournamentEntry[]): void { writeJson(TOURNAMENT_STORAGE_KEY, entries.slice(0, 20)); }

export function getSelectedTheme(): PickitupTheme { return readInventory().selectedTheme; }

export function setSelectedTheme(theme: PickitupTheme): void {
  const inv = readInventory();
  if (!inv.ownedThemes.includes(theme)) return;
  inv.selectedTheme = theme;
  writeInventory(inv);
  applySelectedTheme();
}

function getIconPreview(iconId: string): string {
  const icon = SHOP_ICONS.find(i => i.id === iconId);
  return icon?.preview || '❓';
}

function getIconImageUrl(iconId: string): string | null {
  const icon = SHOP_ICONS.find(i => i.id === iconId);
  return icon?.imageUrl || (icon?.preview.startsWith('/pickitup/') ? icon.preview : null);
}

export function getSymbolOverride(symbol: GameSymbol): string | null {
  const inv = readInventory();
  return inv.iconSlots[symbol] ? getIconPreview(inv.iconSlots[symbol]!) : null;
}

export function getSymbolOverrideTexture(symbol: GameSymbol): { key: string; url: string } | null {
  const inv = readInventory();
  const iconId = inv.iconSlots[symbol];
  if (!iconId) return null;
  const url = getIconImageUrl(iconId);
  return url ? { key: `shop-icon-${iconId}`, url } : null;
}

export function getEquippedIconTextures(): Array<{ key: string; url: string }> {
  const inv = readInventory();
  return Object.values(inv.iconSlots)
    .filter((iconId): iconId is string => Boolean(iconId))
    .map((iconId) => ({ iconId, url: getIconImageUrl(iconId) }))
    .filter((item): item is { iconId: string; url: string } => Boolean(item.url))
    .map((item) => ({ key: `shop-icon-${item.iconId}`, url: item.url }));
}

export function equipIconForSymbol(symbol: GameSymbol, iconId: string | null): void {
  const inv = readInventory();
  if (iconId && !inv.ownedIcons.includes(iconId)) return;
  inv.iconSlots[symbol] = iconId;
  writeInventory(inv);
}

export function getIconSlots(): Record<GameSymbol, string | null> {
  return readInventory().iconSlots;
}

export function applySelectedTheme(): void {
  if (typeof document === 'undefined') return;
  document.body.dataset.pickitupTheme = getSelectedTheme();
}

function weeklyChallenges(progress: { onlineMatches: number; onlineWins: number; onlineScore: number; coinsEarnedOnline: number }): WeeklyChallengeView[] {
  return [
    { id: 'online-15', icon: '🎮', title: 'Online Grinder', description: 'Spiele diese Woche 15 Online-Duelle.', progress: Math.min(progress.onlineMatches, 15), target: 15, reward: 500, completed: progress.onlineMatches >= 15 },
    { id: 'wins-5', icon: '🏆', title: 'Fünf Siege', description: 'Gewinne diese Woche 5 Online-Duelle.', progress: Math.min(progress.onlineWins, 5), target: 5, reward: 750, completed: progress.onlineWins >= 5 },
    { id: 'score-25000', icon: '🔥', title: '25K Score Push', description: 'Sammel online insgesamt 25.000 Punkte.', progress: Math.min(progress.onlineScore, 25000), target: 25000, reward: 1000, completed: progress.onlineScore >= 25000 },
    { id: 'coins-1500', icon: '🪙', title: 'Coin-Jäger', description: 'Verdiene 1.500 Coins nur durch Online-Matches.', progress: Math.min(progress.coinsEarnedOnline, 1500), target: 1500, reward: 1200, completed: progress.coinsEarnedOnline >= 1500 },
  ];
}

export function getFeatureStateView(): FeatureStateView {
  const inv = readInventory();
  const progress = readWeeklyProgress();
  const weekly = weeklyChallenges(progress);

  const themeItems: ShopItemView[] = THEMES.map(item => ({ ...item, owned: inv.ownedThemes.includes(item.theme), equipped: inv.selectedTheme === item.theme }));
  const iconItems: ShopItemView[] = SHOP_ICONS.map(item => ({
    id: item.id,
    title: item.title,
    description: item.description,
    price: item.price,
    preview: item.preview,
    imageUrl: item.imageUrl,
    owned: inv.ownedIcons.includes(item.id),
    equipped: false,
  }));

  return {
    weekKey: progress.weekKey,
    weekly,
    weeklyCompleted: weekly.filter(item => item.completed).length,
    coinBalance: inv.coins,
    selectedTheme: inv.selectedTheme,
    ownedIcons: inv.ownedIcons,
    iconSlots: inv.iconSlots,
    shopItems: [...themeItems, ...iconItems],
    iconConcepts: ICON_CONCEPTS_PIPELINE,
  };
}

export function recordMatchResult(payload: MatchResultPayload): void {
  if (payload.isOffline) return;

  const score = Math.max(0, Math.round(payload.score || 0));
  const name = String(payload.name || 'Spieler').trim().slice(0, 20) || 'Spieler';
  const isWinner = Boolean(payload.isWinner || payload.rank === 1);
  const earnedCoins = Math.min(180, 35 + Math.floor(score / 220) + (isWinner ? 75 : 0));

  const inv = readInventory();
  inv.coins += earnedCoins;
  writeInventory(inv);

  const progress = readWeeklyProgress();
  progress.onlineMatches += 1;
  progress.onlineScore += score;
  progress.coinsEarnedOnline += earnedCoins;
  if (isWinner) progress.onlineWins += 1;
  writeWeeklyProgress(progress);

  const entries = readTournamentEntries();
  const previous = entries.find(e => e.name.toLowerCase() === name.toLowerCase());
  if (previous && previous.score >= score) {
    writeTournamentEntries(entries);
    return;
  }

  const next = entries.filter(e => e.name.toLowerCase() !== name.toLowerCase()).concat({ id: `${name}-${Date.now()}`, name, score, date: new Date().toISOString() }).sort((a, b) => b.score - a.score).slice(0, 20);
  writeTournamentEntries(next);
}
